import { cx, useTranslations } from "@jugl-web/utils";
import React, { useMemo } from "react";

type CRMStatusPillSize = "md" | "sm";

export type CRMStatus =
  | "connected"
  | "accountConnected"
  | "disconnected"
  | "accountDisconnected"
  | "notConnected"
  | "enabled"
  | "disabled"
  | "verified"
  | "notVerified"
  | "pending"
  | "unknown"
  | "high"
  | "medium"
  | "low"
  | "visible"
  | "hidden"
  | "pendingNeedMoreInfo"
  | "pendingSubmission"
  | "expired"
  | "revoked"
  | "rejected"
  | "ineligible"
  | "failed"
  | "pendingReview"
  | "declined"
  | "approved"
  | "accountIsEnabled";

const SECONDARY_STYLE = {
  backgroundColor: "bg-secondary/10",
  dotColor: "bg-secondary",
};

const TERTIARY_STYLE = {
  backgroundColor: "bg-tertiary/10",
  dotColor: "bg-tertiary",
};

const NEUTRAL_STYLE = {
  backgroundColor: "bg-grey-200",
  dotColor: "bg-messages-chat-bubble-sender",
};

const PENDING_STYLE = {
  backgroundColor: "bg-[#FFF7EC]",
  dotColor: "bg-[#F36021]",
};

const STATUS_STYLES: Record<
  CRMStatus,
  { backgroundColor: string; dotColor: string }
> = {
  connected: { ...SECONDARY_STYLE },
  enabled: { ...SECONDARY_STYLE },
  verified: { ...SECONDARY_STYLE },
  accountConnected: { ...SECONDARY_STYLE },
  high: { ...SECONDARY_STYLE },
  visible: { ...SECONDARY_STYLE },

  disconnected: { ...TERTIARY_STYLE },
  disabled: { ...TERTIARY_STYLE },
  notVerified: { ...TERTIARY_STYLE },
  notConnected: { ...TERTIARY_STYLE },
  accountDisconnected: { ...TERTIARY_STYLE },
  low: { ...TERTIARY_STYLE },
  hidden: { ...TERTIARY_STYLE },
  expired: { ...TERTIARY_STYLE },
  failed: { ...TERTIARY_STYLE },
  ineligible: { ...TERTIARY_STYLE },
  rejected: { ...TERTIARY_STYLE },
  revoked: { ...TERTIARY_STYLE },
  declined: { ...TERTIARY_STYLE },
  approved: { ...SECONDARY_STYLE },
  unknown: { ...NEUTRAL_STYLE },
  pending: { ...PENDING_STYLE },
  pendingReview: { ...PENDING_STYLE },
  medium: { ...PENDING_STYLE },
  pendingNeedMoreInfo: { ...PENDING_STYLE },
  pendingSubmission: { ...PENDING_STYLE },
  accountIsEnabled: { ...SECONDARY_STYLE },
};

export const CRMStatusPill: React.FC<{
  status: CRMStatus;
  size?: CRMStatusPillSize;
}> = ({ status, size = "md" }) => {
  const { t } = useTranslations();

  const { label, dotColor, backgroundColor } = useMemo(() => {
    const baseStyle = STATUS_STYLES[status];

    switch (status) {
      case "connected":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.connected",
            defaultMessage: "Connected",
          }),
        };
      case "expired":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.expired",
            defaultMessage: "Expired",
          }),
        };
      case "failed":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.failed",
            defaultMessage: "Failed",
          }),
        };
      case "declined":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.declined",
            defaultMessage: "Declined",
          }),
        };
      case "ineligible":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.ineligible",
            defaultMessage: "Ineligible",
          }),
        };
      case "rejected":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.rejected",
            defaultMessage: "Rejected",
          }),
        };
      case "revoked":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.revoked",
            defaultMessage: "Revoked",
          }),
        };
      case "pendingNeedMoreInfo":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.pending-need-more-info",
            defaultMessage: "Pending - Need more info",
          }),
        };
      case "pendingSubmission":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.pending-submission",
            defaultMessage: "Pending submission",
          }),
        };
      case "pendingReview":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.pending-review",
            defaultMessage: "Pending review",
          }),
        };
      case "high":
        return {
          ...baseStyle,
          label: t({
            id: "common.high",
            defaultMessage: "High",
          }),
        };
      case "medium":
        return {
          ...baseStyle,
          label: t({
            id: "common.medium",
            defaultMessage: "Medium",
          }),
        };
      case "low":
        return {
          ...baseStyle,
          label: t({
            id: "common.low",
            defaultMessage: "Low",
          }),
        };
      case "visible":
        return {
          ...baseStyle,
          label: t({
            id: "common.visible",
            defaultMessage: "Visible",
          }),
        };
      case "hidden":
        return {
          ...baseStyle,
          label: t({
            id: "common.hidden",
            defaultMessage: "Hidden",
          }),
        };
      case "accountConnected":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.account-is-connected",
            defaultMessage: "Account is Connected",
          }),
        };
      case "disconnected":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.disconnected",
            defaultMessage: "Disconnected",
          }),
        };
      case "accountDisconnected":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.account-is-disconnected",
            defaultMessage: "Account is disconnected",
          }),
        };
      case "notConnected":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.not-connected",
            defaultMessage: "Not connected",
          }),
        };
      case "disabled":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.disabled",
            defaultMessage: "Disabled",
          }),
        };
      case "enabled":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.enabled",
            defaultMessage: "Enabled",
          }),
        };
      case "verified":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.verified",
            defaultMessage: "Verified",
          }),
        };
      case "notVerified":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.notVerified",
            defaultMessage: "Not Verified",
          }),
        };
      case "unknown":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.unknown",
            defaultMessage: "Unknown",
          }),
        };
      case "pending":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.pending",
            defaultMessage: "Pending",
          }),
        };
      case "approved":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.approved",
            defaultMessage: "Approved",
          }),
        };
      case "accountIsEnabled":
        return {
          ...baseStyle,
          label: t({
            id: "client-support-page.account-is-enabled",
            defaultMessage: "Account is Enabled",
          }),
        };
      default:
        throw new Error(`Unsupported status: ${status}`);
    }
  }, [status, t]);

  return (
    <div
      className={cx(
        "flex h-7 shrink-0 items-center gap-2 rounded-lg px-2.5",
        size === "md" && "h-10 px-[14px]",
        backgroundColor
      )}
    >
      <div className={cx("h-[9px] w-[9px] rounded-full", dotColor)} />
      <span
        className={cx(
          "font-secondary text-sm tracking-[0.14px] text-dark",
          size === "md" && "font-medium"
        )}
      >
        {label}
      </span>
    </div>
  );
};
